import { DocumentPanel } from '../../components/panels/DocumentPanel';
import type { DocPanel } from '../../lib/types';

const DOC: DocPanel = {
  title: 'Well Prognosis',
  file: 'TAPIS DEEP-1RDR1_WELL PROGNOSIS.pdf',
  page: 1,
  pageCount: 1,
  kind: 'geology',
};

/** The lithology key printed across the top of the prognosis sheet. */
const LITHOLOGY: { label: string; fill: string }[] = [
  { label: 'CONGLOMERATE', fill: 'repeating-linear-gradient(45deg,#3f7f3f 0 3px,#cfe6cf 3px 6px)' },
  { label: 'LIMESTONE', fill: 'repeating-linear-gradient(0deg,#5aa6d8 0 3px,#dbeefb 3px 6px)' },
  { label: 'CHALK', fill: '#f2f7fb' },
  { label: 'ARGILLACEOUS LIMESTONE', fill: 'repeating-linear-gradient(90deg,#5aa6d8 0 3px,#e5f2fa 3px 6px)' },
  { label: 'MARL', fill: 'repeating-linear-gradient(0deg,#7fbf7f 0 2px,#eaf5ea 2px 5px)' },
  { label: 'SANDSTONE', fill: 'repeating-linear-gradient(0deg,#e0c200 0 2px,#fdf6c8 2px 5px)' },
  { label: 'DOLOMITE', fill: 'repeating-linear-gradient(45deg,#2f9e8f 0 3px,#d6f0ec 3px 6px)' },
  { label: 'NO SAMPLE', fill: '#ffffff' },
  { label: 'ARGILLACEOUS DOLOMITE', fill: 'repeating-linear-gradient(135deg,#2f9e8f 0 3px,#e0f3f0 3px 6px)' },
  { label: 'SHALE', fill: 'repeating-linear-gradient(0deg,#7a8b95 0 2px,#e9edef 2px 5px)' },
  { label: 'SILTSTONE', fill: 'repeating-linear-gradient(45deg,#9aa87a 0 3px,#eef1e4 3px 6px)' },
  { label: 'ANHYDRITE', fill: 'repeating-linear-gradient(135deg,#b07fd0 0 3px,#f0e4f7 3px 6px)' },
  { label: 'IGNEOUS', fill: 'repeating-linear-gradient(45deg,#d03b34 0 3px,#fae0de 3px 6px)' },
  { label: 'DOLOMITIC LIMESTONE', fill: 'repeating-linear-gradient(90deg,#2f9e8f 0 3px,#dceff0 3px 6px)' },
  { label: 'CHERT / FLINT', fill: '#e6e2d6' },
  { label: 'CLAYSTONE', fill: 'repeating-linear-gradient(0deg,#9a9a5a 0 2px,#f1f1dd 2px 5px)' },
  { label: 'HALITE', fill: 'repeating-linear-gradient(45deg,#d27fc0 0 3px,#fae4f4 3px 6px)' },
  { label: 'SYLVITE', fill: 'repeating-linear-gradient(135deg,#d03b34 0 3px,#fbe6e5 3px 6px)' },
  { label: 'CALCAREOUS DOLOMITE', fill: 'repeating-linear-gradient(0deg,#2f9e8f 0 2px,#e3f4f2 2px 5px)' },
  { label: 'COAL', fill: '#1b1b1b' },
];

const COLUMNS = [
  'SEISMIC SECTION',
  'MUD PROGRAM & DATA ACQUISITION',
  'CUTTING SAMPLE',
  'CORE / SIDEWALL CORE',
  'STRATIGRAPHY / BIOSTRAT',
  'WIRELINE LOGGING',
  'CASING SCHEME',
  'PROGNOSIS LITHOLOGY',
  'WELL PROGNOSIS NOTE',
];

const PORE_CURVES = [
  'Pore Pressure (Low Case)',
  'RFB',
  'Pore Pressure (Mid Case)',
  'RFB',
  'Pore Pressure (High Case)',
  'RFB',
  'Overburden Gradient',
  'RFB',
  'Fracture Gradient (Low Case)',
  'RFB',
  'Fracture Gradient (Mid Case)',
  'RFB',
  'Fracture Gradient (High Case)',
  'RFB',
];

const DETAILS: [string, string][] = [
  ['Well Name', 'Tapis Deep-1RDR1'],
  ['Field Name', 'Tapis Deep-1RDR1'],
  ['Location', 'Peninsular Malaysia'],
  ['Block', 'PM515'],
  ['Operator', 'Petronas Carigali Sdn. Bhd'],
  ['Surface Location', 'Lat: 5° 30′ 29.345″ N & Long: 105° 1′ 28.448″ E'],
  ['Rig Name / Type', 'Naga-2 / Jack-up'],
  ['Rig Contractor', 'Velesto'],
  ['DF Elevation', '30.00 m'],
  ['Water Depth', '65.00 m'],
  ['Spud Date', '27/06/2026'],
];

export function WellPrognosis() {
  return (
    <div className="splitpane splitpane--wideleft">
      <DocumentPanel doc={DOC} onPrev={() => undefined} onNext={() => undefined}>
        <article className="paper prognosis" aria-label="Well prognosis log">
          <h3 className="prognosis-title">WELL PROGNOSIS LOG</h3>

          <section className="lithokey" aria-label="Lithology symbols">
            <h4>LITHOLOGY SYMBOLS</h4>
            <ul>
              {LITHOLOGY.map((l) => (
                <li key={l.label}>
                  <span className="lithokey-swatch" style={{ background: l.fill }} />
                  {l.label}
                </li>
              ))}
            </ul>
          </section>

          <div className="prognosis-tracks">
            {COLUMNS.map((c) => (
              <div className="prognosis-col" key={c}>
                <span className="prognosis-col-title">{c}</span>
              </div>
            ))}

            <div className="prognosis-col prognosis-col--pressure">
              <span className="prognosis-col-title">PROGNOSIS PORE PRESSURE</span>
              <ul className="prognosis-curves">
                {PORE_CURVES.map((c, i) => (
                  <li key={`${c}-${i}`}>{c}</li>
                ))}
              </ul>
            </div>
          </div>

          <p className="prognosis-note">Potential to encounter shallow hazard zone.</p>
        </article>
      </DocumentPanel>

      <section className="panel panel--pad" aria-label="Well details">
        <h2 className="summary-title">Well Details</h2>
        <dl className="details">
          {DETAILS.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
          <div>
            <dt>Working Interest</dt>
            <dd>
              <table className="details-table">
                <thead>
                  <tr>
                    <th>Party</th>
                    <th>Percent</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Petronas Carigali Sdn. Bhd</td>
                    <td>100</td>
                  </tr>
                </tbody>
              </table>
            </dd>
          </div>
        </dl>
      </section>
    </div>
  );
}
